import {
  AssetType,
  AutomatedParameter,
  AutomatedParameterInput,
  BatteryDataType,
  OptimizationParameterValue,
  PeakWindow,
  PeakWindowType,
  Season,
} from '@e2m-customer-portal/domains';
import { mustBeGreaterThan, mustBeLessThan, Nullable } from '@e2m-customer-portal/shared';
import { InfoTooltip, NumberInput, ValidatedContainer } from '@e2m-customer-portal/ui';
import { faker } from '@faker-js/faker/locale/en';
import { Button } from '@mui/material';
import { format } from 'date-fns';
import { equals } from 'ramda';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BatteryProps {
  data: BatteryDataType;
  identifier: string;
  setUpdatedItemsData: Dispatch<SetStateAction<Map<string, Map<string, OptimizationParameterValue>>>>;
  updatedItemsData: Map<string, Map<string, OptimizationParameterValue>> | undefined;
  setSaveBarOpen: Dispatch<SetStateAction<boolean>>;
  setIsValid: Dispatch<SetStateAction<boolean>>;
  resetTrigger?: boolean;
  assetType?: AssetType;
  readOnly: (parameter?: string) => boolean;
}

export function Battery({
  data,
  identifier,
  updatedItemsData,
  setUpdatedItemsData,
  setSaveBarOpen,
  setIsValid,
  resetTrigger,
  assetType,
  readOnly,
}: Readonly<BatteryProps>) {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');

  const inputClass = 'w-full min-w-4';

  const [updatedData, setUpdatedData] = useState(data);
  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());
  const [peakWindows, setPeakWindows] = useState<PeakWindowType[] | undefined>(data.peak_windows);

  const resetValues = () => {
    setUpdatedData(data);
    setPeakWindows(data.peak_windows);
    setSaveBarOpen(false);
    setChanges(new Map());
  };

  useEffect(() => {
    if (
      assetType === AssetType.PeakShavingBatteries &&
      peakWindows &&
      updatedItemsData &&
      !equals(updatedData.peak_windows, peakWindows)
    ) {
      setUpdatedData({ ...updatedData, peak_windows: peakWindows });
      changes.set('peak_windows', peakWindows);
      setUpdatedItemsData(updatedItemsData.set(identifier, changes));
    }
  }, [peakWindows]);

  useEffect(() => {
    if (changes && updatedItemsData) {
      setUpdatedItemsData(updatedItemsData.set(identifier, changes));
    }
  }, [changes]);

  useEffect(() => {
    if (updatedItemsData?.size === 0) {
      setChanges(new Map());
    }
  }, [updatedItemsData]);

  useEffect(resetValues, [resetTrigger]);

  return (
    <div className="h-fit p-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 border-solid border border-divider rounded-md">
      <h3 className="m-0 flex flex-col whitespace-nowrap xl:col-span-2">
        <span className="text-xs font-normal text-grey-300">{t('COPT_BATTERIES_TITLE')}</span>
        <span className="inline-flex items-center pt-1 text-lg font-medium">
          {updatedData.alias}
          <InfoTooltip text={t('COPT_BATTERIES_ALIAS_TOOLTIP')} field="alias" />
        </span>
      </h3>
      <ValidatedContainer validate="on" onValidated={setIsValid}>
        {(updatedData.power_charge !== undefined ||
          updatedData.power_discharge !== undefined ||
          updatedData.use_dynamic_power_limits !== undefined) && (
          <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
            {t('COPT_BATTERIES_POWER_TITLE')}
          </h4>
        )}
        {updatedData.power_charge !== undefined && (
          <NumberInput
            value={updatedData.power_charge === null ? null : Math.round(updatedData.power_charge * 1000)}
            className={inputClass}
            required
            name="power_charge"
            label={t('COPT_BATTERIES_POWER_CHARGE')}
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const power_charge = value != null && !Number.isNaN(value) ? value / 1000 : null;
              setUpdatedData({
                ...updatedData,
                power_charge,
              });
              setChanges(changes.set('power_charge', power_charge));
            }}
            disabled={readOnly('power_charge')}
            notNegative
            endAdornment={<InfoTooltip field="power_charge" objectType="batteries" />}
          />
        )}
        {updatedData.power_discharge !== undefined && (
          <NumberInput
            value={updatedData.power_discharge === null ? null : Math.round(updatedData.power_discharge * 1000)}
            className={inputClass}
            required
            name="power_discharge"
            label={t('COPT_BATTERIES_POWER_DISCHARGE')}
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const power_discharge = value != null && !Number.isNaN(value) ? value / 1000 : null;
              setUpdatedData({
                ...updatedData,
                power_discharge,
              });
              setChanges(changes.set('power_discharge', power_discharge));
            }}
            disabled={readOnly('power_discharge')}
            notNegative
            endAdornment={<InfoTooltip field="power_discharge" objectType="batteries" />}
          />
        )}
        {updatedData.use_dynamic_power_limits !== undefined && (
          <label className="text-sm font-light mr-4 flex items-center xl:col-span-2">
            <input
              type="checkbox"
              checked={!!updatedData.use_dynamic_power_limits}
              disabled={readOnly('use_dynamic_power_limits')}
              onChange={() => {
                setSaveBarOpen(true);
                const use_dynamic_power_limits = !updatedData.use_dynamic_power_limits;
                setUpdatedData({ ...updatedData, use_dynamic_power_limits });
                setChanges(changes.set('use_dynamic_power_limits', use_dynamic_power_limits));
              }}
              className="w-4 h-4 mr-2 accent-green-200"
            />
            {t('COPT_BATTERIES_USE_DYNAMIC_POWER_LIMITS')}
            <InfoTooltip field="use_dynamic_power_limits" objectType="batteries" />
          </label>
        )}
        {(updatedData.energy_capacity !== undefined || updatedData.nominal_capacity) && (
          <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
            {t('COPT_BATTERIES_ENERGY_CAPACITY_TITLE')}
          </h4>
        )}
        {updatedData.energy_capacity !== undefined && (
          <NumberInput
            value={updatedData.energy_capacity === null ? null : Math.round(updatedData.energy_capacity * 1000)}
            className={inputClass}
            name="energy_capacity"
            label={t('COPT_BATTERIES_ENERGY_CAPACITY')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const energy_capacity = value != null && !Number.isNaN(value) ? Math.round(value) / 1000 : null;
              setUpdatedData({
                ...updatedData,
                energy_capacity,
              });
              setChanges(changes.set('energy_capacity', energy_capacity));
            }}
            disabled={readOnly('energy_capacity')}
            notNegative
            endAdornment={<InfoTooltip field="energy_capacity" objectType="batteries" />}
          />
        )}
        {updatedData.nominal_capacity !== undefined && (
          <NumberInput
            value={updatedData.nominal_capacity === null ? null : Math.round(updatedData.nominal_capacity * 1000)}
            className={inputClass}
            name="nominal_capacity"
            label={t('COPT_BATTERIES_NOMINAL_CAPACITY')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const nominal_capacity = value != null && !Number.isNaN(value) ? Math.round(value) / 1000 : null;
              setUpdatedData({
                ...updatedData,
                nominal_capacity,
              });
              setChanges(changes.set('nominal_capacity', nominal_capacity));
            }}
            disabled={readOnly('nominal_capacity')}
            notNegative
            endAdornment={<InfoTooltip field="nominal_capacity" objectType="batteries" />}
          />
        )}
        {(updatedData.efficiency_in !== undefined ||
          updatedData.efficiency_out !== undefined ||
          updatedData.energy_efficiency_in !== undefined ||
          updatedData.energy_efficiency_out !== undefined) && (
          <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
            {t('COPT_BATTERIES_EFFICIENCY_TITLE')}
          </h4>
        )}
        {updatedData.efficiency_in !== undefined && (
          <NumberInput
            value={updatedData.efficiency_in === null ? null : Math.round(updatedData.efficiency_in * 100)}
            className={inputClass}
            name="efficiency_in"
            label={t('COPT_BATTERIES_EFFICIENCY_IN')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const efficiency_in = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                efficiency_in,
              });
              setChanges(changes.set('efficiency_in', efficiency_in));
            }}
            disabled={readOnly('efficiency_in')}
            notNegative
            rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
            endAdornment={<InfoTooltip field="efficiency_in" objectType="batteries" />}
          />
        )}
        {updatedData.efficiency_out !== undefined && (
          <NumberInput
            value={updatedData.efficiency_out === null ? null : Math.round(updatedData.efficiency_out * 100)}
            className={inputClass}
            name="efficiency_out"
            label={t('COPT_BATTERIES_EFFICIENCY_OUT')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const efficiency_out = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                efficiency_out,
              });
              setChanges(changes.set('efficiency_out', efficiency_out));
            }}
            disabled={readOnly('efficiency_out')}
            notNegative
            rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
            endAdornment={<InfoTooltip field="efficiency_out" objectType="batteries" />}
          />
        )}
        {updatedData.energy_efficiency_in !== undefined && (
          <NumberInput
            value={
              updatedData.energy_efficiency_in === null ? null : Math.round(updatedData.energy_efficiency_in * 100)
            }
            className={inputClass}
            name="energy_efficiency_in"
            label={t('COPT_BATTERIES_ENERGY_EFFICIENCY_IN')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const energy_efficiency_in = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                energy_efficiency_in,
              });
              setChanges(changes.set('energy_efficiency_in', energy_efficiency_in));
            }}
            disabled={readOnly('energy_efficiency_in')}
            notNegative
            rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
            endAdornment={<InfoTooltip field="energy_efficiency_in" objectType="batteries" />}
          />
        )}
        {updatedData.energy_efficiency_out !== undefined && (
          <NumberInput
            value={
              updatedData.energy_efficiency_out === null ? null : Math.round(updatedData.energy_efficiency_out * 100)
            }
            className={inputClass}
            name="energy_efficiency_out"
            label={t('COPT_BATTERIES_ENERGY_EFFICIENCY_OUT')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const energy_efficiency_out = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                energy_efficiency_out,
              });
              setChanges(changes.set('energy_efficiency_out', energy_efficiency_out));
            }}
            disabled={readOnly('energy_efficiency_out')}
            notNegative
            rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
            endAdornment={<InfoTooltip field="energy_efficiency_out" objectType="batteries" />}
          />
        )}
        {updatedData.costs_per_full_cycle !== undefined && (
          <div className="flex flex-col gap-y-4 border-solid border-0 border-t border-divider xl:col-span-2">
            <h4 className="m-0 pt-3 text-sm font-semibold">{t('COPT_BATTERIES_COSTS')}</h4>
            <AutomatedParameterInput
              parameter={updatedData.costs_per_full_cycle}
              className={inputClass}
              name="costs_per_full_cycle"
              label={t('COPT_BATTERIES_COSTS_PER_FULL_CYCLE')}
              required
              notNegative
              onParameterUpdate={(costs_per_full_cycle: AutomatedParameter<number>) => {
                setSaveBarOpen(true);
                setUpdatedData({
                  ...updatedData,
                  costs_per_full_cycle,
                });
                setChanges(changes.set('costs_per_full_cycle', costs_per_full_cycle));
              }}
              disabled={readOnly('costs_per_full_cycle')}
              endAdornment={<InfoTooltip field="costs_per_full_cycle" objectType="batteries" />}
            />
          </div>
        )}
        {(updatedData.storage_max !== undefined ||
          updatedData.storage_min !== undefined ||
          updatedData.accepted_capacity_deviation !== undefined) && (
          <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
            {t('COPT_BATTERIES_STORAGE')}
          </h4>
        )}
        {updatedData.storage_max !== undefined && (
          <NumberInput
            value={updatedData.storage_max === null ? null : Math.round(updatedData.storage_max * 100)}
            className={inputClass}
            name="storage_max"
            label={t('COPT_BATTERIES_STORAGE_MAX')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const storage_max = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                storage_max,
              });
              setChanges(changes.set('storage_max', storage_max));
            }}
            disabled={readOnly('storage_max')}
            notNegative
            maxValue={100}
            endAdornment={<InfoTooltip field="storage_max" objectType="batteries" />}
          />
        )}
        {updatedData.storage_min !== undefined && (
          <NumberInput
            value={updatedData.storage_min === null ? null : Math.round(updatedData.storage_min * 100)}
            className={inputClass}
            name="storage_min"
            label={t('COPT_BATTERIES_STORAGE_MIN')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const storage_min = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                storage_min,
              });
              setChanges(changes.set('storage_min', storage_min));
            }}
            disabled={readOnly('storage_min')}
            notNegative
            endAdornment={<InfoTooltip field="storage_min" objectType="batteries" />}
          />
        )}
        {updatedData.accepted_capacity_deviation !== undefined && (
          <div className="xl:col-span-2">
            <NumberInput
              value={
                updatedData.accepted_capacity_deviation === null
                  ? null
                  : Math.round(updatedData.accepted_capacity_deviation * 100)
              }
              className={inputClass}
              name="accepted_capacity_deviation"
              label={t('COPT_BATTERIES_ACCEPTED_CAPACITY_DEVIATION')}
              required
              onChange={(value: number) => {
                setSaveBarOpen(true);
                const accepted_capacity_deviation =
                  value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                setUpdatedData({
                  ...updatedData,
                  accepted_capacity_deviation,
                });
                setChanges(changes.set('accepted_capacity_deviation', accepted_capacity_deviation));
              }}
              disabled={readOnly('accepted_capacity_deviation')}
              notNegative
              rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
              endAdornment={<InfoTooltip field="accepted_capacity_deviation" objectType="batteries" />}
            />
          </div>
        )}
        {(updatedData.cycle_per_year !== undefined ||
          updatedData.cycle_per_day !== undefined ||
          updatedData.prequalified_power !== undefined) && (
          <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
            {t('COPT_BATTERIES_CYCLE')}
          </h4>
        )}
        {updatedData.cycle_per_year !== undefined && (
          <NumberInput
            value={updatedData.cycle_per_year}
            className={inputClass}
            name="cycle_per_year"
            label={t('COPT_BATTERIES_CYCLE_PER_YEAR')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const cycle_per_year = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                cycle_per_year,
              });
              setChanges(changes.set('cycle_per_year', cycle_per_year));
            }}
            disabled={readOnly('cycle_per_year')}
            minValue={50}
            endAdornment={<InfoTooltip field="cycle_per_year" objectType="batteries" />}
          />
        )}
        {updatedData.cycle_per_day !== undefined && (
          <NumberInput
            value={updatedData.cycle_per_day}
            className={inputClass}
            name="cycle_per_day"
            label={t('COPT_BATTERIES_CYCLE_PER_DAY')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const cycle_per_day = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                cycle_per_day,
              });
              setChanges(changes.set('cycle_per_day', cycle_per_day));
            }}
            disabled={readOnly('cycle_per_day')}
            rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
            endAdornment={<InfoTooltip field="cycle_per_day" objectType="batteries" />}
          />
        )}
        {updatedData.prequalified_power !== undefined && (
          <div className="xl:col-span-2">
            <NumberInput
              value={updatedData.prequalified_power === null ? null : Math.round(updatedData.prequalified_power * 1000)}
              className={inputClass}
              name="prequalified_power"
              label={t('COPT_BATTERIES_PREQUALIFIED_POWER')}
              required
              onChange={(value: number) => {
                setSaveBarOpen(true);
                const prequalified_power = value != null && !Number.isNaN(value) ? Math.round(value) / 1000 : null;
                setUpdatedData({
                  ...updatedData,
                  prequalified_power,
                });
                setChanges(changes.set('prequalified_power', prequalified_power));
              }}
              disabled={readOnly('prequalified_power')}
              notNegative
              endAdornment={<InfoTooltip field="prequalified_power" objectType="batteries" />}
            />
          </div>
        )}
        {updatedData.fcr_storage_reserve_time !== undefined && (
          <>
            <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
              {t('COPT_BATTERIES_FCR_RESERVE')}
            </h4>
            <div className="xl:col-span-2">
              <NumberInput
                value={updatedData.fcr_storage_reserve_time === null ? null : updatedData.fcr_storage_reserve_time * 60}
                className={inputClass}
                name="fcr_storage_reserve_time"
                label={t('COPT_BATTERIES_FCR_STORAGE_RESERVE_TIME')}
                required
                onChange={(value: number) => {
                  setSaveBarOpen(true);
                  const fcr_storage_reserve_time = value != null && !Number.isNaN(value) ? value / 60 : null;
                  setUpdatedData({
                    ...updatedData,
                    fcr_storage_reserve_time,
                  });
                  setChanges(changes.set('fcr_storage_reserve_time', fcr_storage_reserve_time));
                }}
                disabled={readOnly('fcr_storage_reserve_time')}
                notNegative
                endAdornment={<InfoTooltip field="fcr_storage_reserve_time" objectType="batteries" />}
              />
            </div>
          </>
        )}
        {(updatedData.afrr_activation_reserve_time_neg !== undefined ||
          updatedData.afrr_activation_reserve_time_pos !== undefined) && (
          <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
            {t('COPT_BATTERIES_AFRR_RESERVE')}
          </h4>
        )}
        {updatedData.afrr_activation_reserve_time_pos !== undefined && (
          <NumberInput
            value={
              updatedData.afrr_activation_reserve_time_pos === null
                ? null
                : updatedData.afrr_activation_reserve_time_pos * 60
            }
            className={inputClass}
            name="afrr_activation_reserve_time_pos"
            label={t('COPT_BATTERIES_AFRR_ACTIVATION_RESERVE_TIME_POS')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const afrr_activation_reserve_time_pos = value != null && !Number.isNaN(value) ? value / 60 : null;
              setUpdatedData({
                ...updatedData,
                afrr_activation_reserve_time_pos,
              });
              setChanges(changes.set('afrr_activation_reserve_time_pos', afrr_activation_reserve_time_pos));
            }}
            disabled={readOnly('afrr_activation_reserve_time_pos')}
            notNegative
            endAdornment={<InfoTooltip field="afrr_activation_reserve_time_pos" objectType="batteries" />}
          />
        )}
        {updatedData.afrr_activation_reserve_time_neg !== undefined && (
          <NumberInput
            value={
              updatedData.afrr_activation_reserve_time_neg === null
                ? null
                : updatedData.afrr_activation_reserve_time_neg * 60
            }
            className={inputClass}
            name="afrr_activation_reserve_time_neg"
            label={t('COPT_BATTERIES_AFRR_ACTIVATION_RESERVE_TIME_NEG')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const afrr_activation_reserve_time_neg = value != null && !Number.isNaN(value) ? value / 60 : null;
              setUpdatedData({
                ...updatedData,
                afrr_activation_reserve_time_neg,
              });
              setChanges(changes.set('afrr_activation_reserve_time_neg', afrr_activation_reserve_time_neg));
            }}
            disabled={readOnly('afrr_activation_reserve_time_neg')}
            notNegative
            endAdornment={<InfoTooltip field="afrr_activation_reserve_time_neg" objectType="batteries" />}
          />
        )}
        {peakWindows && !!peakWindows.length && (
          <div className="pt-3 flex justify-between border-solid border-0 border-t border-divider xl:col-span-2">
            <h4 className="m-0 text-sm font-semibold">
              {t('COPT_BATTERIES_PEAK_WINDOWS')}
              <InfoTooltip field="peak_windows" text={t('COPT_BATTERIES_PEAK_WINDOWS_TOOLTIP')} />
            </h4>
            <Button
              color="primary"
              className="text-2xl flex h-10 w-10 min-w-10 shrink-0 grow-0 items-center justify-center rounded-full"
              disabled={readOnly('peak_windows')}
              onClick={() => {
                setSaveBarOpen(true);
                setPeakWindows([
                  ...peakWindows,
                  {
                    season: {
                      start_date: format(new Date().toISOString(), 'yyyy-MM-dd'),
                      end_date: format(new Date().toISOString(), 'yyyy-MM-dd'),
                      name: 'autumn' as Season,
                    },
                    start_time_str: '00:00',
                    end_time_str: '00:00',
                    start_soc: 0,
                    end_soc: 0,
                    identifier: faker.string.uuid(),
                    exclude_weekends: false,
                    additional_exclude_dates: null,
                    federal_state_holidays: null,
                    exclude_last_days_of_the_year: false,
                  },
                ]);
              }}
            >
              +
            </Button>
          </div>
        )}
        {!!peakWindows?.length &&
          peakWindows?.map((window) => (
            <div className="xl:col-span-2" key={window.identifier}>
              <PeakWindow
                data={window}
                peakWindows={peakWindows}
                setPeakWindows={setPeakWindows}
                setSaveBarOpen={setSaveBarOpen}
                readOnly={readOnly}
                setIsValid={setIsValid}
              />
            </div>
          ))}
      </ValidatedContainer>
    </div>
  );
}
